import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type BankAccountList,
  type BankAccountUpdateBankAccountData,
  type BankAccountUpdateBankAccountError,
  type BankAccountWithDetails,
  type Options,
  bankAccountListBankAccountsQueryKey,
  bankAccountUpdateBankAccount,
} from '@column/api';

import { bankAccountDropdownListBaseQueryKey } from './bankAccountDropdownQueryKey';

const LIST_QUERY_ID = bankAccountListBankAccountsQueryKey()[0]._id;

const matchesListKey = (queryKey: QueryKey): boolean => {
  const head = queryKey[0] as { _id?: string } | undefined;
  return head?._id === LIST_QUERY_ID;
};

export const useUpdateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BankAccountWithDetails,
    BankAccountUpdateBankAccountError,
    Options<BankAccountUpdateBankAccountData>
  >({
    mutationFn: async (variables) => {
      const { data } = await bankAccountUpdateBankAccount({ ...variables, throwOnError: true });
      return data;
    },
    onSuccess: (account) => {
      const entries = queryClient.getQueriesData<BankAccountList>({
        predicate: ({ queryKey }) => matchesListKey(queryKey),
      });
      entries.forEach(([queryKey, data]) => {
        if (!data) return;
        const index = data.bank_accounts.findIndex((entry) => entry.id === account.id);
        if (index === -1) return;
        const next = data.bank_accounts.slice();
        next[index] = account;
        queryClient.setQueryData<BankAccountList>(queryKey, { ...data, bank_accounts: next });
      });
      queryClient.invalidateQueries({ queryKey: bankAccountDropdownListBaseQueryKey });
    },
  });
};
